import React, {
  createContext,
  useEffect,
  useContext,
  useCallback,
  useState,
  PropsWithChildren,
  useMemo,
} from "react";
import {
  deleteStoredPwaInstallStatus,
  getStoredPwaInstallStatus,
  setStoredPwaInstallStatus,
} from "../utils/storage";
import { AppType, checkOs } from "../utils/helpers";
import { showSuccessToast } from "../utils/show-toast";
import Logger from "../utils/logger";
import { OperatingSystems } from "../utils/vendor";

// eslint-disable-next-line no-unused-vars, no-shadow
enum INSTALL_PROMPT_EVENT_OUTCOME {
  // eslint-disable-next-line no-unused-vars
  ACCEPTED = "accepted",
  // eslint-disable-next-line no-unused-vars
  DISMISSED = "dismissed",
}

interface ExtendedNavigatorObject extends Navigator {
  getInstalledRelatedApps?: () => Promise<
    {
      id: string;
      platform: string;
      url: string;
    }[]
  >;
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: INSTALL_PROMPT_EVENT_OUTCOME;
    platform: string;
  }>;
  prompt(): Promise<{ outcome: INSTALL_PROMPT_EVENT_OUTCOME }>;
}

interface PwaContextValue {
  appInstallHasStarted: boolean;
  appInstallHasCompleted: boolean;
  userIsInPwa: boolean;
  promptEvent: Event | null;
  doesRelatedAppExist: (appType?: AppType) => Promise<boolean | undefined>;
  promptUserToInstall: () => void;
  resetInstallationParams: () => void;
}

const PwaContext = createContext<PwaContextValue>({} as PwaContextValue);

function PwaContextProvider({ children }: PropsWithChildren) {
  const [promptEvent, setPromptEvent] = useState<Event | null>(null);
  const [appInstallHasStarted, setAppInstallHasStarted] =
    useState<boolean>(false);
  const [appInstallHasCompleted, setAppInstallHasCompleted] =
    useState<boolean>(false);
  const [userIsInPwa, setUserIsInPwa] = useState<boolean>(false);

  const disableInAppInstallPrompt = () => {
    setPromptEvent(null);
  };

  // Would come in handy when the PWA installation/automatic opening handoff is complete
  // This will prevent the PWA from continuously being automatically opened when the user visits via the browser subsequent times after the point of installation.
  const resetInstallationParams = useCallback(() => {
    setAppInstallHasStarted(false);
    setAppInstallHasCompleted(false);
  }, []);

  // Prompt user to install PWA
  const promptUserToInstall = useCallback(async () => {
    if (!promptEvent) {
      return;
    }
    const result = await (promptEvent as BeforeInstallPromptEvent).prompt();
    if (result.outcome === INSTALL_PROMPT_EVENT_OUTCOME.ACCEPTED) {
      setAppInstallHasStarted(true);
      setStoredPwaInstallStatus({
        openedPwa: false,
        installationDate: Date.now(),
      });
      disableInAppInstallPrompt();
      showSuccessToast("Installation in progress");
    }
  }, [promptEvent]);

  // Check if the native app or PWA exists on the device
  const doesRelatedAppExist = useCallback(async (appType?: AppType) => {
    const windowNavigator: ExtendedNavigatorObject = navigator;
    if (!windowNavigator.getInstalledRelatedApps) return;

    let relatedAppToFind = appType;
    if (!appType) {
      const os = checkOs();
      relatedAppToFind =
        os === OperatingSystems.Android ? AppType.ANDROID : AppType.IOS;
    }

    const relatedApps = await windowNavigator.getInstalledRelatedApps();
    const appExists = !!relatedApps.find(
      (app) => app.platform === relatedAppToFind
    );
    if (relatedAppToFind === AppType.PWA) {
      const pwaInstallStatus = getStoredPwaInstallStatus();
      return appExists || !!pwaInstallStatus;
    }
    return appExists;
  }, []);

  // Hijack the PWA install prompt from the browser on load.
  useEffect(() => {
    const interceptInstallPrompt = (event: Event) => {
      event.preventDefault();
      if (event) {
        deleteStoredPwaInstallStatus();
        setPromptEvent(event);
      }
    };

    window.addEventListener("beforeinstallprompt", interceptInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", interceptInstallPrompt);
    };
  }, []);

  // Check if the PWA installation has started.
  // If it has started, set the related state and clear the in-app install prompt.
  useEffect(() => {
    const handleAppInstallHasStarted = () => {
      setAppInstallHasStarted(true);
      disableInAppInstallPrompt();
    };

    window.addEventListener("appinstalled", handleAppInstallHasStarted);

    return () => {
      window.removeEventListener("appinstalled", handleAppInstallHasStarted);
    };
  }, []);

  // Check if the PWA installation has finished.
  useEffect(() => {
    // eslint-disable-next-line no-undef
    let checkInstallationCompleteInterval: NodeJS.Timeout;
    if (appInstallHasStarted) {
      checkInstallationCompleteInterval = setInterval(async () => {
        const pwaExists = await doesRelatedAppExist(AppType.PWA);
        if (pwaExists) setAppInstallHasCompleted(true);
      }, 5000);
    }

    return () => {
      clearInterval(checkInstallationCompleteInterval);
    };
  }, [appInstallHasStarted, doesRelatedAppExist]);

  // Detect if the user is viewing the app from the PWA
  useEffect(() => {
    const isInPwaIos =
      "standalone" in window.navigator && window.navigator.standalone;
    const isInPwaChrome = window.matchMedia(
      "(display-mode: standalone)"
    ).matches;
    const queryParams = new URLSearchParams(window.location.search);
    const sourceQueryIsSet = queryParams.get("source") === "pwa";
    if (isInPwaChrome || isInPwaIos || sourceQueryIsSet) {
      setUserIsInPwa(true);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      appInstallHasStarted,
      appInstallHasCompleted,
      promptEvent,
      userIsInPwa,
      doesRelatedAppExist,
      promptUserToInstall,
      resetInstallationParams,
    }),
    [
      appInstallHasStarted,
      appInstallHasCompleted,
      doesRelatedAppExist,
      userIsInPwa,
      promptEvent,
      promptUserToInstall,
      resetInstallationParams,
    ]
  );

  return (
    <PwaContext.Provider value={contextValue}>{children}</PwaContext.Provider>
  );
}

function usePwaContext() {
  const pwaContext = useContext(PwaContext);

  if (!pwaContext) {
    Logger.log("usePwaContext must be used within a PwaContext provider");
  }
  return pwaContext;
}

export { PwaContextProvider, usePwaContext };
